// src/stores/contact-store.ts
import { sendContactMessageApi } from '@/lib/api/contactApi';
import { type ContactFormValues, SUBJECTS, contactSchema } from '@/lib/validation/contact.schema';
import { toast } from 'sonner';
import { z } from 'zod';
import { create } from 'zustand';

type FieldErrors = Partial<Record<keyof ContactFormValues, string>>;

interface ContactState {
  values: ContactFormValues;
  errors: FieldErrors;
  isSubmitting: boolean;
  isSuccess: boolean;
  serverError: string | null;

  setField: <K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) => void;
  setCaptchaToken: (token: string) => void;
  reset: () => void;
  validate: () => boolean;
  submit: () => Promise<void>;
}

const initialValues: ContactFormValues = {
  fullName: '',
  email: '',
  phone: '',
  subject: SUBJECTS[0],
  message: '',
  captchaToken: '',
};

function zodToFieldErrors(err: z.ZodError): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of err.issues) {
    const key = issue.path[0] as keyof ContactFormValues | undefined;
    if (key && !out[key]) out[key] = issue.message;
  }
  return out;
}

export const useContactStore = create<ContactState>((set, get) => ({
  values: initialValues,
  errors: {},
  isSubmitting: false,
  isSuccess: false,
  serverError: null,

  setField: (key, value) =>
    set(state => ({
      values: { ...state.values, [key]: value },
      errors: { ...state.errors, [key]: undefined },
      serverError: null,
      isSuccess: false,
    })),

  setCaptchaToken: (token: string) =>
    set(state => ({
      values: { ...state.values, captchaToken: token },
      errors: { ...state.errors, captchaToken: undefined },
      serverError: null,
    })),

  reset: () => set({ values: initialValues, errors: {}, isSubmitting: false, isSuccess: false, serverError: null }),

  validate: () => {
    const result = contactSchema.safeParse(get().values);
    if (!result.success) {
      set({ errors: zodToFieldErrors(result.error) });
      return false;
    }
    set({ errors: {} });
    return true;
  },

  submit: async () => {
    set({ serverError: null, isSuccess: false });

    if (!get().validate()) {
      toast.error('Veuillez corriger les champs en rouge.');
      return;
    }

    set({ isSubmitting: true });
    const toastId = toast.loading('Envoi du message…');

    try {
      const res = await sendContactMessageApi(get().values);
      toast.success(res.message, { id: toastId });
      set({ isSuccess: true, values: initialValues, errors: {} });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Erreur inconnue';
      toast.error(message, { id: toastId });
      set({ serverError: message });
    } finally {
      set({ isSubmitting: false });
    }
  },
}));
